import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { OrganisationMember } from '../../../../../server/dist/types/src/types/PublicEntityTypes';
import { useUserInfo } from '../../../contexts/user-context/use-user-info';
import { Button } from '../../../design-system/button/button';
import { ErrorSection } from '../../../design-system/error-section/error-section';
import { Inline } from '../../../design-system/inline/inline';
import { LoadingSection } from '../../../design-system/loading/loading';
import { Stack } from '../../../design-system/stack/stack';
import { postSwrFetcher } from '../../../utils/post-swr-fetcher';
import { InviteMemberModal } from './invite-member-modal';
import { MembersTable } from './members-table';

const MEMBERS_API_PATH = '/api/organisations/members';
const INVITE_MEMBER_API_PATH = '/api/organisations/invite';

export const ManageMembers = () => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { authenticatedInfo } = useUserInfo();
  const loggedInUserEmail = authenticatedInfo?.email ?? '';

  const {
    data: membersData,
    error: swrError,
    isLoading,
    mutate,
  } = useSWR<OrganisationMember[], Error>(MEMBERS_API_PATH);

  const { trigger: inviteMember } = useSWRMutation(INVITE_MEMBER_API_PATH, postSwrFetcher);

  const handleInviteMember = async (email: string, firstName: string, lastName: string) => {
    try {
      setIsSubmitting(true);
      await inviteMember({ email, firstName, lastName });
      setOpen(false);
      toast.success(`Invite sent to ${firstName} ${lastName} at ${email}`);
      mutate();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          email,
          firstName,
          lastName,
        },
      });
      if (error instanceof AxiosError && error.response?.data.message === 'Member has already been invited') {
        toast.error(`A user with the email ${email} already exists or has been invited`);
      } else {
        toast.error('Error inviting member');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <LoadingSection text="Loading..." />;
  }

  if (!membersData) {
    return <ErrorSection error={swrError} />;
  }

  return (
    <Stack align="stretch" className="px-6 pt-4">
      <Inline justify="between" className="h-[42px]">
        <h1 className="text-2xl font-semibold text-black">Team</h1>
        <Button iconLeft={<UserPlus />} onClick={() => setOpen(true)}>
          Add Member
        </Button>
      </Inline>
      <InviteMemberModal
        open={open}
        onOpenChange={setOpen}
        handleInviteMember={handleInviteMember}
        isLoading={isSubmitting}
      />
      <MembersTable members={membersData ?? []} loggedInUserEmail={loggedInUserEmail} />
    </Stack>
  );
};
